import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid } from '@mui/material';
import DataTable from '@/components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import Switch from 'components/Common/Switch';
import TimePicker from 'components/Common/TimePicker';
import { MdOutlineContentCopy } from 'react-icons/md';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';

interface GoogleCalendarFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const normalizeSelectOptions = (options: unknown) => {
  if (Array.isArray(options)) return options;
  return [];
};

const GoogleCalendarForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: GoogleCalendarFormProps) => {
  const providersField = configFields?.find((field: any) => field.key === 'providers');
  const otherFields = configFields?.filter((field: any) => field.key !== 'providers');
  const {
    fields: providers,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.providers`
  });
  const watchedProviders = useWatch({
    control,
    name: `${namePrefix}.providers`
  });

  useEffect(() => {
    const existingProviders = getValues(`${namePrefix}.providers`);

    if (!existingProviders || existingProviders.length === 0) {
      append({
        name: '',
        calendar: '',
        conflict_calendar_ids: [],
        slot: 30,
        min_notice: 0,
        max_days: 0,
        services: [
          {
            name: '',
            description: '',
            duration: '',
            service_types: []
          }
        ],
        bookable_hours: defaultHours()
      });
    }
  }, [append, getValues, namePrefix]);

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}
      {providersField && (
        <Box>
          <Typography fontSize={16} fontWeight={600} my={2}>
            Provider Details
          </Typography>

          {providers.map((provider: any, index: number) =>
            (() => {
              const selectedCalendarIds = (watchedProviders || [])
                .map((p: any, i: number) => (i === index ? null : p?.calendar_id))
                .filter((id: any) => Boolean(id));
              const currentCalendarId = watchedProviders?.[index]?.calendar_id;
              return (
                <ProviderItem
                  key={provider.id}
                  index={index}
                  provider={provider}
                  control={control}
                  remove={remove}
                  disabled={disabled}
                  getValues={getValues}
                  setValue={setValue}
                  namePrefix={namePrefix}
                  providersFields={providersField}
                  totalProviders={providers.length}
                  selectedCalendarIds={selectedCalendarIds}
                  currentCalendarId={currentCalendarId}
                />
              );
            })()
          )}

          {/* Add Provider */}
          <Box display='flex' justifyContent='center'>
            <Button
              variant='contained'
              disabled={disabled}
              onClick={() =>
                append({
                  name: '',
                  calendar: '',
                  conflict_calendar_ids: [],
                  slot: 30,
                  min_notice: 0,
                  max_days: 0,
                  services: [],
                  bookable_hours: defaultHours()
                })
              }
            >
              + Add Another Provider
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default GoogleCalendarForm;

const ProviderItem = ({
  index,
  control,
  remove,
  disabled,
  getValues,
  setValue,
  namePrefix,
  totalProviders,
  providersFields,
  selectedCalendarIds,
  currentCalendarId
}: any) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;

  const {
    fields: services,
    append: addService,
    remove: removeService
  } = useFieldArray({
    control,
    name: `${providerPrefix}.services`
  });
  const calendarField = providersFields?.group?.find((f: any) => f.key === 'calendar_id');
  const conflictCalendarsField = providersFields?.group?.find((f: any) => f.key === 'conflict_calendar_ids');
  const genderField = providersFields?.group?.find((f: any) => f.key === 'provider_gender');
  const languagesKnownField = providersFields?.group?.find((f: any) => f.key === 'languages_known');
  const bookingSlotField = providersFields?.group?.find((f: any) => f.key === 'booking_slot');
  const servicesField = providersFields?.group?.find((f: any) => f.key === 'services');
  const serviceTypesField = servicesField?.group?.find((f: any) => f.key === 'service_types');
  const serviceTypeOptions = serviceTypesField?.options || [
    { label: 'In-Person', value: 'Inperson' },
    { label: 'Virtual', value: 'Virtual' }
  ];
  const genderOptions = [
    { label: 'Select', value: '' },
    ...(normalizeSelectOptions(genderField?.options).length
      ? normalizeSelectOptions(genderField?.options)
      : [
          { label: 'Male', value: 'Male' },
          { label: 'Female', value: 'Female' }
        ])
  ];
  const allCalendarOptions = normalizeSelectOptions(calendarField?.options);

  const watchedServices = useWatch({
    control,
    name: `${providerPrefix}.services`
  });

  const serviceOptions = (watchedServices || []).flatMap((service: any, sIndex: number) => {
    if (!service?.name) return [];

    return [
      {
        label: service.name,
        value: `service_${sIndex + 1}`
      }
    ];
  });

  const calendarOptions = allCalendarOptions.filter((option: any) => {
    const optionValue = option?.value;
    if (optionValue === currentCalendarId) return true;
    return !selectedCalendarIds?.includes(optionValue);
  });
  const conflictCalendarOptions = normalizeSelectOptions(conflictCalendarsField?.options || calendarField?.options);
  const bookingSlotOptions = normalizeSelectOptions(bookingSlotField?.options);

  useEffect(() => {
    if (services.length === 0) {
      addService({ name: '', description: '', duration: '', service_types: [] });
    }
  }, [services.length, addService]);

  const columns = useMemo(
    (): ColumnDef<any>[] => [
      {
        header: 'DAY',
        accessorKey: 'label'
      },
      {
        header: 'STATUS',
        cell: (info) => {
          const day = info.row.original.value;
          return <Switch disabled={disabled} name={`${providerPrefix}.bookable_hours.${day}.open`} control={control} />;
        }
      },
      {
        header: 'TIME',
        cell: (info) => {
          const day = info.row.original.value;
          return (
            <Box display='flex' gap={2} flexWrap='wrap'>
              <TimePicker
                name={`${providerPrefix}.bookable_hours.${day}.from_time`}
                control={control}
                disabled={disabled}
              />
              <TimePicker
                name={`${providerPrefix}.bookable_hours.${day}.to_time`}
                control={control}
                disabled={disabled}
              />
            </Box>
          );
        }
      },
      {
        header: 'SERVICE',
        cell: (info) => {
          const day = info.row.original.value;
          return (
            <Select
              label='Services'
              name={`${providerPrefix}.bookable_hours.${day}.services`}
              control={control}
              disabled={disabled}
              options={serviceOptions}
              multiple
              fullWidth
            />
          );
        }
      },
      {
        header: 'ACTION',
        cell: (info) => (
          <CopyAction
            dayKey={info.row.original.value}
            disabled={disabled}
            fieldName={`${providerPrefix}.bookable_hours`}
            getValues={getValues}
            setValue={setValue}
          />
        )
      }
    ],
    [control, disabled, getValues, providerPrefix, serviceOptions, setValue]
  );

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>
        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`${providerPrefix}.provider_name`}
            control={control}
            label='Provider Name'
            placeholder='Enter Provider Name'
            disabled={disabled}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            label={genderField?.label || 'Gender'}
            name={`${providerPrefix}.provider_gender`}
            control={control}
            disabled={disabled}
            options={genderOptions}
            displayEmpty
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`${providerPrefix}.languages_known`}
            control={control}
            label={languagesKnownField?.label || 'Languages Known'}
            placeholder='English, Spanish, Hindi'
            disabled={disabled}
            helperText={languagesKnownField?.description}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            label={calendarField?.label || 'Write Calendar'}
            name={`${providerPrefix}.calendar_id`}
            control={control}
            disabled={disabled}
            options={calendarOptions}
            helperText={
              calendarField?.description ||
              'Select the calendar where new events will be created, updated, and deleted.'
            }
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            label={conflictCalendarsField?.label || 'Conflict Calendars'}
            name={`${providerPrefix}.conflict_calendar_ids`}
            control={control}
            disabled={disabled}
            options={conflictCalendarOptions}
            multiple
            helperText={
              conflictCalendarsField?.description ||
              'Select one or more calendars to check for scheduling conflicts before creating or updating an event. If no calendars are selected, the Write Calendar will be used for conflict detection by default.'
            }
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            label={bookingSlotField?.label || 'Booking Slot'}
            name={`${providerPrefix}.booking_slot`}
            control={control}
            disabled={disabled}
            options={bookingSlotOptions}
            required={bookingSlotField?.required}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`${providerPrefix}.x_min_notice_hours`}
            control={control}
            label='Minimum Notice (Hours)'
            placeholder='Enter Minimum Notice (Hours)'
            disabled={disabled}
            type='number'
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`${providerPrefix}.x_booking_horizon_days`}
            control={control}
            label='Advance Booking Limit (Days)'
            placeholder='Enter Advance Booking Limit'
            disabled={disabled}
            type='number'
            fullWidth
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <Box display='flex' justifyContent='space-between' mb={2}>
          <Typography fontWeight={500}>Services</Typography>

          <Button
            disabled={disabled}
            size='small'
            variant='contained'
            onClick={() => addService({ name: '', description: '', duration: '', service_types: [] })}
          >
            Add Service
          </Button>
        </Box>

        {services.map((service: any, sIndex: number) => {
          const servicePrefix = `${providerPrefix}.services.${sIndex}`;

          return (
            <Grid container spacing={2} alignItems='center' mb={2} key={service.id}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  name={`${servicePrefix}.name`}
                  control={control}
                  label='Service Name'
                  placeholder='Enter Service Name'
                  disabled={disabled}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  name={`${servicePrefix}.description`}
                  control={control}
                  label='Service Description'
                  placeholder='Enter Service Description'
                  disabled={disabled}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <Input
                  name={`${servicePrefix}.duration`}
                  control={control}
                  label='Service duration'
                  placeholder='e.g. 30 (minutes)'
                  type='number'
                  disabled={disabled}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Select
                  label={serviceTypesField?.label || 'Service Types'}
                  name={`${servicePrefix}.service_types`}
                  control={control}
                  disabled={disabled}
                  options={serviceTypeOptions}
                  multiple
                  fullWidth
                />
              </Grid>

              {services?.length > 1 && !disabled && (
                <Grid size='auto'>
                  <IconButton color='error' onClick={() => removeService(sIndex)}>
                    <RiDeleteBin6Line />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          );
        })}
      </Box>

      <Typography fontWeight={500} mt={2} my={2}>
        Working Hours
      </Typography>

      <DataTable data={daysOfWeek} columns={columns} />
    </Box>
  );
};

const daysOfWeek = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' }
];

const defaultHours = () => {
  const obj: any = {};
  daysOfWeek.forEach((day) => {
    obj[day.value] = {
      open: true,
      from_time: '',
      to_time: '',
      services: []
    };
  });
  return obj;
};

const CopyAction = ({ dayKey, fieldName, getValues, setValue, disabled }: any) => {
  const handleCopy = () => {
    const source = getValues(`${fieldName}.${dayKey}`);

    daysOfWeek.forEach((day) => {
      const target = `${fieldName}.${day.value}`;
      setValue(`${target}.open`, source?.open);
      setValue(`${target}.from_time`, source?.from_time);
      setValue(`${target}.to_time`, source?.to_time);
      setValue(`${target}.services`, source?.services);
    });
  };

  return (
    <IconButton onClick={handleCopy} disabled={disabled}>
      <MdOutlineContentCopy size={18} />
    </IconButton>
  );
};
